import type { IsoTimestamp, Uuid } from "../types/base.js";
import type { Trip } from "../types/entities.js";

/**
 * Ein Einkauf entsteht beim Betreten des Einkaufsmodus, nicht erst beim
 * Abschließen. Nur so kann die Familie erfahren, dass jemand gerade im Laden
 * steht, und noch schnell etwas auf die Liste setzen.
 *
 * Damit entsteht ein neues Problem: Wer im Laden kurz zurück auf die Liste
 * geht und wieder in den Einkaufsmodus, darf keinen zweiten Einkauf anlegen.
 * Sonst stünde die Meldung "X kauft gerade ein" zweimal auf dem anderen
 * Telefon und die Historie hätte zwei halbe Einkäufe statt einem.
 */

/** Wie lange ein offener Einkauf als derselbe Gang durch den Laden gilt. */
export const TRIP_REUSE_HOURS = 4;

export interface ReuseTripInput {
  trips: readonly Trip[];
  storeId: Uuid | null;
  now: IsoTimestamp;
  hours?: number;
}

/**
 * Der offene Einkauf, den dieser Besuch fortsetzt, oder `null` für einen neuen.
 *
 * Offen heißt: kein `finished_at`, kein Grabstein, derselbe Laden und noch
 * keine vier Stunden alt. Ein vergessener Einkauf von gestern wird dadurch
 * nicht wiederbelebt, er bleibt einfach liegen: Er taucht weder in der
 * Historie noch in den Ausgaben auf, beide sehen nur abgeschlossene Einkäufe.
 */
export function findReusableTrip(input: ReuseTripInput): Trip | null {
  const grenze = Date.parse(input.now) - (input.hours ?? TRIP_REUSE_HOURS) * 3600_000;

  const offene = input.trips
    .filter(
      (trip) =>
        trip.deleted_at === null &&
        trip.finished_at === null &&
        trip.store_id === input.storeId &&
        Date.parse(trip.started_at) >= grenze,
    )
    .sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at));

  return offene[0] ?? null;
}
